import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUsage } from '../services/api';
import { useAuth } from './AuthContext';

const PlanContext = createContext(null);

export const usePlan = () => {
  const ctx = useContext(PlanContext);
  if (!ctx) return { usage: null, loading: true, refresh: () => {} };
  return ctx;
};

export const PlanProvider = ({ children }) => {
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const usageRes = await getUsage();
      setUsage(usageRes);
    } catch (err) {
      console.error('Failed to load usage data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) refresh();
    else setLoading(false);
  }, [user, refresh]);

  const canCreateProject = usage ? (usage.max_projects < 0 || usage.project_count < usage.max_projects) : true;
  const canAddUser = usage ? (usage.max_users < 0 || usage.user_count < usage.max_users) : true;
  const hasFeature = (feature) => usage?.features?.[feature] ?? false;
  const isReadOnly = usage?.access_status === 'read_only' || usage?.access_status === 'suspended';
  const accessStatus = user?.access_status || usage?.access_status || 'active';

  return (
    <PlanContext.Provider value={{
      usage,
      loading,
      refresh,
      canCreateProject,
      canAddUser,
      hasFeature,
      isReadOnly,
      accessStatus,
      planName: accessStatus === 'trial' ? 'Trial' : (accessStatus === 'active' ? 'Active' : accessStatus),
    }}>
      {children}
    </PlanContext.Provider>
  );
};

export default PlanContext;
